/**
 * FilterSheet
 *
 * Bottom sheet modal for filtering recipes.
 * Manages 4 filter groups: Diet, Duration, Rating, and Meal Type.
 * Keeps local draft state and only commits to parent on Apply.
 */

import React, { useState, useEffect } from 'react';
import {
  View,
  StyleSheet,
  Modal,
  TouchableOpacity,
  Text,
  ScrollView,
  Platform,
} from 'react-native';
import { useTranslation } from 'react-i18next';
import Feather from '@expo/vector-icons/Feather';
import {
  Paprika,
  PaprikaTypography,
  PaprikaSpacing,
  PaprikaRadius,
} from '../../styles/theme';

export type FilterState = {
  diets: Array<'veg' | 'fish' | 'meat'>;
  maxMinutes: number | null; // 15 | 30 | 45 | 60 | null
  minRating: number | null; // 4.5 | 4.8 | 5.0 | null
  mealTypes: Array<'breakfast' | 'lunch' | 'dinner' | 'dessert'>;
};

export const DEFAULT_FILTERS: FilterState = {
  diets: [],
  maxMinutes: null,
  minRating: null,
  mealTypes: [],
};

interface FilterSheetProps {
  visible: boolean;
  onClose: () => void;
  filters: FilterState;
  onApply: (filters: FilterState) => void;
  resultCount: number;
  /**
   * Optional: compute the result count for arbitrary filters. When provided, the
   * "See N recipes" CTA reflects the live draft selection instead of the
   * already-applied `resultCount`.
   */
  countForFilters?: (filters: FilterState) => number;
}

const DIET_DOT_COLORS: Record<'veg' | 'fish' | 'meat', string> = {
  veg: '#5d7c43',
  fish: '#cf6f56',
  meat: Paprika.primary,
};

const DURATION_VALUES = [15, 30, 45, 60] as const;
const RATING_VALUES = [4.5, 4.8, 5.0] as const;
const MEAL_TYPE_VALUES = ['breakfast', 'lunch', 'dinner', 'dessert'] as const;

export const FilterSheet: React.FC<FilterSheetProps> = ({
  visible,
  onClose,
  filters,
  onApply,
  resultCount: appliedResultCount,
  countForFilters,
}) => {
  const { t } = useTranslation();
  const [draftFilters, setDraftFilters] = useState<FilterState>(filters);

  // Live count for the CTA: reflects the in-progress draft selection when a
  // count function is supplied, otherwise falls back to the applied count.
  const resultCount = countForFilters ? countForFilters(draftFilters) : appliedResultCount;

  // Sync draft state when parent filters change (modal opens)
  useEffect(() => {
    if (visible) {
      setDraftFilters(filters);
    }
  }, [visible, filters]);

  // Handle diet chip toggle
  const toggleDiet = (diet: 'veg' | 'fish' | 'meat') => {
    setDraftFilters(prev => {
      const isSelected = prev.diets.includes(diet);
      if (isSelected) {
        return { ...prev, diets: prev.diets.filter(d => d !== diet) };
      } else {
        return { ...prev, diets: [...prev.diets, diet] };
      }
    });
  };

  // Handle diet "All" chip (clears all)
  const resetDiets = () => {
    setDraftFilters(prev => ({ ...prev, diets: [] }));
  };

  // Handle duration chip (single select)
  const setDuration = (minutes: number | null) => {
    setDraftFilters(prev => ({ ...prev, maxMinutes: minutes }));
  };

  // Handle rating chip (single select)
  const setRating = (rating: number | null) => {
    setDraftFilters(prev => ({ ...prev, minRating: rating }));
  };

  // Handle meal type toggle
  const toggleMealType = (mealType: 'breakfast' | 'lunch' | 'dinner' | 'dessert') => {
    setDraftFilters(prev => {
      const isSelected = prev.mealTypes.includes(mealType);
      if (isSelected) {
        return { ...prev, mealTypes: prev.mealTypes.filter(m => m !== mealType) };
      } else {
        return { ...prev, mealTypes: [...prev.mealTypes, mealType] };
      }
    });
  };

  // Reset all filters to default
  const handleReset = () => {
    setDraftFilters(DEFAULT_FILTERS);
  };

  // Apply and close
  const handleApply = () => {
    onApply(draftFilters);
    onClose();
  };

  return (
    <Modal
      visible={visible}
      animationType="slide"
      transparent={true}
      onRequestClose={onClose}
    >
      {/* Backdrop */}
      <TouchableOpacity
        style={styles.backdrop}
        activeOpacity={1}
        onPress={onClose}
      />

      {/* Sheet panel */}
      <View style={styles.sheetPanel}>
        {/* Drag handle */}
        <View style={styles.dragHandleContainer}>
          <View style={styles.dragHandle} />
        </View>

        {/* Header row */}
        <View style={styles.headerRow}>
          <Text style={styles.headerTitle}>{t('recipes.filters.title')}</Text>
          <TouchableOpacity onPress={handleReset}>
            <Text style={styles.resetButton}>{t('recipes.filters.reset')}</Text>
          </TouchableOpacity>
        </View>

        {/* Scrollable content */}
        <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
          {/* Diet group */}
          <View style={styles.filterGroup}>
            <Text style={styles.filterLabel}>{t('recipes.filters.diet.label')}</Text>
            <View style={styles.chipsRow}>
              {/* All chip */}
              <Chip
                label={t('recipes.filters.diet.all')}
                selected={draftFilters.diets.length === 0}
                onPress={resetDiets}
              />
              {/* Individual diet chips */}
              {(['veg', 'fish', 'meat'] as const).map(diet => (
                <DietChip
                  key={diet}
                  diet={diet}
                  label={t(`recipes.filters.diet.${diet}`)}
                  selected={draftFilters.diets.includes(diet)}
                  onPress={() => toggleDiet(diet)}
                />
              ))}
            </View>
          </View>

          {/* Duration group */}
          <View style={styles.filterGroup}>
            <Text style={styles.filterLabel}>{t('recipes.filters.duration.label')}</Text>
            <View style={styles.chipsRow}>
              {/* All chip */}
              <Chip
                label={t('recipes.filters.duration.all')}
                selected={draftFilters.maxMinutes === null}
                onPress={() => setDuration(null)}
              />
              {/* Duration chips */}
              {DURATION_VALUES.map(minutes => (
                <Chip
                  key={minutes}
                  label={t(`recipes.filters.duration.${minutes}`)}
                  selected={draftFilters.maxMinutes === minutes}
                  onPress={() => setDuration(minutes)}
                />
              ))}
            </View>
          </View>

          {/* Rating group */}
          <View style={styles.filterGroup}>
            <Text style={styles.filterLabel}>{t('recipes.filters.rating.label')}</Text>
            <View style={styles.chipsRow}>
              {/* All chip */}
              <Chip
                label={t('recipes.filters.rating.all')}
                selected={draftFilters.minRating === null}
                onPress={() => setRating(null)}
              />
              {/* Rating chips */}
              {RATING_VALUES.map(rating => (
                <Chip
                  key={rating}
                  label={t(`recipes.filters.rating.${rating.toFixed(1)}`)}
                  selected={draftFilters.minRating === rating}
                  onPress={() => setRating(rating)}
                />
              ))}
            </View>
          </View>

          {/* Meal type group */}
          <View style={[styles.filterGroup, { marginBottom: PaprikaSpacing.lg }]}>
            <Text style={styles.filterLabel}>{t('recipes.filters.mealType.label')}</Text>
            <View style={styles.chipsRow}>
              {MEAL_TYPE_VALUES.map(mealType => (
                <Chip
                  key={mealType}
                  label={t(`recipes.filters.mealType.${mealType}`)}
                  selected={draftFilters.mealTypes.includes(mealType)}
                  onPress={() => toggleMealType(mealType)}
                />
              ))}
            </View>
          </View>
        </ScrollView>

        {/* CTA button */}
        <TouchableOpacity
          style={styles.ctaButton}
          onPress={handleApply}
          activeOpacity={0.8}
        >
          <Text style={styles.ctaButtonText}>
            {t('recipes.filters.apply', { count: resultCount })}
          </Text>
        </TouchableOpacity>
      </View>
    </Modal>
  );
};

/**
 * Chip component for standard filters (duration, rating)
 */
interface ChipProps {
  label: string;
  selected: boolean;
  onPress: () => void;
}

const Chip: React.FC<ChipProps> = ({ label, selected, onPress }) => {
  return (
    <TouchableOpacity
      style={[
        styles.chip,
        selected && styles.chipSelected,
        !selected && styles.chipUnselected,
      ]}
      onPress={onPress}
      activeOpacity={0.7}
    >
      <Text
        style={[
          styles.chipText,
          selected && styles.chipTextSelected,
          !selected && styles.chipTextUnselected,
        ]}
      >
        {label}
      </Text>
    </TouchableOpacity>
  );
};

/**
 * DietChip component for diet filters (includes colored dot)
 */
interface DietChipProps {
  diet: 'veg' | 'fish' | 'meat';
  label: string;
  selected: boolean;
  onPress: () => void;
}

const DietChip: React.FC<DietChipProps> = ({ diet, label, selected, onPress }) => {
  const dotColor = DIET_DOT_COLORS[diet];

  return (
    <TouchableOpacity
      style={[
        styles.chip,
        selected && styles.chipSelected,
        !selected && styles.chipUnselected,
      ]}
      onPress={onPress}
      activeOpacity={0.7}
    >
      <View style={[styles.dietDot, { backgroundColor: dotColor }]} />
      <Text
        style={[
          styles.chipText,
          selected && styles.chipTextSelected,
          !selected && styles.chipTextUnselected,
        ]}
      >
        {label}
      </Text>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  backdrop: {
    position: 'absolute',
    inset: 0,
    backgroundColor: 'rgba(26,18,10,0.36)',
  },
  sheetPanel: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: Paprika.background,
    borderTopLeftRadius: PaprikaRadius.xl,
    borderTopRightRadius: PaprikaRadius.xl,
    paddingTop: PaprikaSpacing.sm,
    paddingHorizontal: PaprikaSpacing.lg,
    paddingBottom: PaprikaSpacing.lg + 16, // +16 for safe area
    ...Platform.select({
      ios: {
        shadowColor: '#52443c',
        shadowOffset: { width: 0, height: -4 },
        shadowOpacity: 0.1,
        shadowRadius: 16,
      },
      android: {
        elevation: 8,
      },
    }),
  },
  dragHandleContainer: {
    alignItems: 'center',
    marginBottom: PaprikaSpacing.md,
  },
  dragHandle: {
    width: 40,
    height: 5,
    backgroundColor: '#ece0d4',
    borderRadius: 999,
  },
  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: PaprikaSpacing.lg,
  },
  headerTitle: {
    ...PaprikaTypography.headingMedium,
    color: Paprika.textPrimary,
    fontFamily: 'Newsreader-Regular',
    fontSize: 23,
    letterSpacing: -0.01,
  },
  resetButton: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 13.5,
    fontWeight: '600',
    color: Paprika.primary,
  },
  content: {
    flex: 1,
  },
  filterGroup: {
    marginBottom: PaprikaSpacing.lg,
  },
  filterLabel: {
    ...PaprikaTypography.labelMedium,
    fontFamily: 'Inter-SemiBold',
    fontSize: 12.5,
    color: Paprika.textSecondary,
    marginBottom: PaprikaSpacing.md,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
  },
  chipsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: PaprikaSpacing.sm,
  },
  chip: {
    paddingVertical: 10,
    paddingHorizontal: 15,
    borderRadius: PaprikaRadius.md,
    flexDirection: 'row',
    alignItems: 'center',
    gap: PaprikaSpacing.sm,
  },
  chipSelected: {
    backgroundColor: Paprika.primary,
  },
  chipUnselected: {
    backgroundColor: Paprika.surfaceLight,
    borderWidth: 1,
    borderColor: Paprika.border,
  },
  chipText: {
    fontFamily: 'Inter-Medium',
    fontSize: 13,
    fontWeight: '500',
  },
  chipTextSelected: {
    color: '#fff',
  },
  chipTextUnselected: {
    color: Paprika.textSecondary,
  },
  dietDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  ctaButton: {
    width: '100%',
    height: 52,
    borderRadius: PaprikaRadius.lg,
    backgroundColor: Paprika.primary,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: PaprikaSpacing.lg,
  },
  ctaButtonText: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 15,
    fontWeight: '600',
    color: '#fff',
  },
});

export default FilterSheet;
